import { HTTP_INTERCEPTORS, provideHttpClient } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { inject } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  type ApolloClientOptions,
  InMemoryCache,
  type NormalizedCacheObject,
} from '@apollo/client/core';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { NgxPermissionsModule } from 'ngx-permissions';
import { ToastrModule } from 'ngx-toastr';
import { environment } from 'src/environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthLayoutComponent } from './layouts/auth/auth-layout.component';
import { DashboardLayoutComponent } from './layouts/dashboard/dashboard-layout.component';
import { AuthGuard } from './shared/guards/auth.guard';
import { TokenInterceptor } from './shared/interceptors/token.interceptor';
import { UnauthorizedErrorInterceptor } from './shared/interceptors/unauthorized.interceptor';
import { AuthService } from './shared/services/auth.service';
import { SharedDataService } from './shared/services/shared/shared-data.service';
import { SharedModule } from './shared/shared.module';

function getGraphQlEndpoint() {
  const isPr = window.location.hostname.startsWith('pr-');
  if (!isPr) {
    return environment.gqlUrl;
  }

  const { hostname } = window.location;
  const { protocol } = window.location;
  const { port } = window.location;

  const gqlUrl = new URL(environment.gqlUrl);
  const path = gqlUrl.pathname;

  const parts = hostname.split('.');

  if (parts.length > 1) {
    parts[1] = parts[1].replace(environment.appPrefix, environment.apiPrefix);
  }
  const modifiedHostname = parts.join('.');

  return `${protocol}//${modifiedHostname}${port ? `:${port}` : ''}${path}`;
}

@NgModule({
  declarations: [AppComponent, AuthLayoutComponent, DashboardLayoutComponent],
  imports: [
    SharedModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ToastrModule.forRoot(),
    NgxPermissionsModule.forRoot(),
  ],
  providers: [
    AuthService,
    AuthGuard,
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedErrorInterceptor,
      multi: true,
    },
    provideHttpClient(),
    provideApollo(() => {
      const gqlUrl = getGraphQlEndpoint();
      const httpLink = inject(HttpLink);

      const options: ApolloClientOptions<NormalizedCacheObject> = {
        link: httpLink.create({ uri: gqlUrl }),
        cache: new InMemoryCache(),
      };

      return options;
    }),
    SharedDataService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
